'use client';

import { useState, useEffect } from 'react';
import { Box, TextField, CircularProgress, MenuItem, Grid, Button, InputAdornment } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import PickerField from '../../../components/PickerField';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useCompanyDataList } from '../../../stores/companyStore';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { createVehicleLicense, updateVehicleLicense, getVehicleLicenseById } from '../../../stores/VehicleLicenseStore';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import { useLanguage } from '../../../utils/i18n';

const VehicleLicenseAddEdit = ({ open, id, onClose, onSuccess, onError, onSubmitEnd = () => {} }) => {
  const { t } = useLanguage();
  const [initialValues, setInitialValues] = useState({
    company: '',
    company_name: '',
    license_name: '',
    periode_notification: 'day',
    threshold: '',
    threshold_danger: '',
    value_periode_notification: ''
  });

  const [loading, setLoading] = useState(Boolean(id));
  const notify = useNotifier();
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const fetchData = async () => {
      if (!id) {
        setInitialValues({
          company: '',
          company_name: '',
          license_name: '',
          periode_notification: 'day',
          threshold: '',
          threshold_danger: '',
          value_periode_notification: ''
        });
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getVehicleLicenseById(id);
        if (!mounted) return;
        if (data) {
          setInitialValues({
            company: data?.company != null ? String(data.company) : '',
            company_name: data?.company_name ?? data.join?.company_name ?? '',
            license_name: data?.license_name ?? '',
            periode_notification: data?.periode_notification ?? 'day',
            threshold: data?.threshold ?? '',
            threshold_danger: data?.threshold_danger ?? '',
            value_periode_notification: data?.value_periode_notification ?? ''
          });
        }
      } catch {
        notify({ message: 'Gagal memuat data', severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [id, open, notify]);

  useEffect(() => {
    if (id || !open) return;

    let storedCompany = null;
    try {
      const rawUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed) {
          storedCompany = parsed.company ?? parsed.company_id ?? null;
        }
      }
    } catch (e) {
      console.log(e);
    }

    if (storedCompany == null) {
      try {
        const rawCompany = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
        if (rawCompany && String(rawCompany) !== 'all') storedCompany = rawCompany;
      } catch (e) {
        console.log(e);
      }
    }
    if (storedCompany == null) return;

    const stored = String(storedCompany);
    const match = companyPickerParams.options.find((c) => String(c.value) === stored);
    if (match) {
      setInitialValues((prev) => ({
        ...prev,
        company: String(match.value),
        company_name: match.label ?? match.name ?? String(match.value)
      }));
    } else {
      setInitialValues((prev) => ({
        ...prev,
        company: stored,
        company_name: stored
      }));
    }
  }, [companyPickerParams.options, id, open]);

  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required('Company is mandatory'),
    license_name: Yup.string().required('License Name is mandatory'),
    periode_notification: Yup.string().required('Periode is mandatory'),
    value_periode_notification: Yup.number().required('Value is mandatory'),
    threshold: Yup.number().min(0, 'Minimum 0').required('Threshold Warning is mandatory'),
    threshold_danger: Yup.number()
      .min(0, 'Minimum 0')
      .required('Threshold Danger is mandatory')
      .test('danger-less-than-warning', 'Threshold Danger must be smaller than Warning', function (value) {
        const { threshold } = this.parent;
        if (value === undefined || threshold === undefined) return true;
        return value < threshold;
      })
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          ...values,
          license_name: formatUpperCase(values.license_name),
          company: Number(values.company),
          threshold: Number(values.threshold),
          threshold_danger: Number(values.threshold_danger),
          value_periode_notification: Number(values.value_periode_notification)
        };
        let response;

        if (id) {
          response = await updateVehicleLicense(id, payload);
        } else {
          response = await createVehicleLicense(payload);
        }
        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }

        notify({ message: id ? 'Updated Successfully' : 'Created Successfully', severity: 'success' });
        resetForm();
        onClose && onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
        onSubmitEnd();
      }
    }
  });

  const dialogActions = (
    <>
      <Button
        variant="contained"
        color="primary"
        type="submit"
        form="vehicle-license-form"
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          textTransform: 'none',
          borderRadius: 1,
          px: 3,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
      <Button onClick={onClose} disabled={formik.isSubmitting}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={id ? t('vehicleLicense.edit', 'Edit Vehicle License') : t('vehicleLicense.add', 'Add Vehicle License')}
      maxWidth="sm"
      fullHeight={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, minHeight: 200, alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <form id="vehicle-license-form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 6, md: 6 }}>
                <PickerField
                  label={`${t('general.company', 'Company')} *`}
                  value={formik.values.company}
                  {...companyPickerParams}
                  onChange={(opt) => {
                    const val = typeof opt === 'object' ? opt?.value : opt;
                    formik.setFieldValue('company', val);
                  }}
                  getOptionId={(o) => o?.value}
                  getOptionLabel={(o) => o?.label}
                  dialogTitle={t('general.selectCompany', 'Select Company')}
                  error={formik.touched.company && Boolean(formik.errors.company)}
                  helperText={formik.touched.company && formik.errors.company}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <TextField
                  label={`${t('vehicleLicense.licenseName', 'License Name')} *`}
                  name="license_name"
                  value={formik.values.license_name}
                  onChange={(e) => {
                    formik.setFieldValue('license_name', formatUpperCase(e.target.value));
                  }}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={formik.touched.license_name && Boolean(formik.errors.license_name)}
                  helperText={formik.touched.license_name && formik.errors.license_name}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <TextField
                  label={`${t('vehicleLicense.thresholdWarning', 'Threshold Warning')} *`}
                  name="threshold"
                  type="number"
                  value={formik.values.threshold}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={formik.touched.threshold && Boolean(formik.errors.threshold)}
                  helperText={formik.touched.threshold && formik.errors.threshold}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{t('vehicleLicense.days', 'Days')}</InputAdornment>
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <TextField
                  label={`${t('vehicleLicense.thresholdDanger', 'Threshold Danger')} *`}
                  name="threshold_danger"
                  type="number"
                  value={formik.values.threshold_danger}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={formik.touched.threshold_danger && Boolean(formik.errors.threshold_danger)}
                  helperText={formik.touched.threshold_danger && formik.errors.threshold_danger}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{t('vehicleLicense.days', 'Days')}</InputAdornment>
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <TextField
                  label={`${t('vehicleLicense.frequencyNotif', 'Frequency Notif')} *`}
                  name="value_periode_notification"
                  type="number"
                  value={formik.values.value_periode_notification}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={formik.touched.value_periode_notification && Boolean(formik.errors.value_periode_notification)}
                  helperText={formik.touched.value_periode_notification && formik.errors.value_periode_notification}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <TextField
                  select
                  label={`${t('vehicleLicense.periodeNotification', 'Periode Notification')} *`}
                  name="periode_notification"
                  value={formik.values.periode_notification}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={formik.touched.periode_notification && Boolean(formik.errors.periode_notification)}
                  helperText={formik.touched.periode_notification && formik.errors.periode_notification}
                >
                  <MenuItem value="day">{t('vehicleLicense.day', 'Day')}</MenuItem>
                  <MenuItem value="week">{t('vehicleLicense.week', 'Week')}</MenuItem>
                  <MenuItem value="month">{t('vehicleLicense.month', 'Month')}</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default VehicleLicenseAddEdit;
